/**
 * Discrepancies List Page
 * 
 * Main page component for displaying and managing discrepancies.
 */

import { renderDiscrepancyList, initDiscrepancyListHandlers } from '../../components/discrepancies/DiscrepancyList.js';
import { apiClient } from '../../services/api.js';
import { t } from '../../i18n/translations.js';

/**
 * Map frontend status values to backend status values for API queries
 * Backend: open, investigating, resolved, accepted, disputed
 * Frontend: pending, under_review, resolved, dismissed
 */
function mapFrontendStatusToBackend(status) {
  const statusMap = {
    pending: 'open',
    under_review: 'investigating',
    resolved: 'resolved',
    dismissed: 'accepted',
  };
  return statusMap[status] || status;
}

/**
 * Map backend status values to frontend status values for display
 */
function mapBackendStatusToFrontend(status) {
  const statusMap = {
    open: 'pending',
    investigating: 'under_review',
    resolved: 'resolved',
    accepted: 'dismissed',
    disputed: 'under_review',
  };
  return statusMap[status] || status;
}

/**
 * Page state
 */
let state = {
  discrepancies: [],
  filters: {},
  pagination: { page: 1, totalPages: 1, totalItems: 0 },
  view: 'card',
  stats: {},
  loading: true,
  error: null,
};

/**
 * Update page state and re-render
 */
function setState(newState) {
  state = { ...state, ...newState };
  render();
}

/**
 * Fetch discrepancies from API
 */
async function fetchDiscrepancies(page = 1, filters = {}) {
  setState({ loading: true, error: null });

  try {
    // Map frontend status filter to backend status value
    const apiFilters = { ...filters };
    if (apiFilters.status) {
      apiFilters.status = mapFrontendStatusToBackend(apiFilters.status);
    }

    const params = new URLSearchParams({
      page: page.toString(),
      pageSize: '20',
      ...apiFilters,
    });

    const response = await apiClient.get(`/v1/discrepancies?${params}`);

    // Handle wrapped API response: { success, data: { items, pageInfo } }
    const data = response.data || response;
    // Ensure items is always an array
    let items = data.items || data.data || data;
    if (!Array.isArray(items)) {
      items = [];
    }
    const pageInfo = data.pageInfo || {};

    // Map backend discrepancy items to frontend format
    items = items.map(item => ({
      ...item,
      // Map backend 'status' to frontend 'resolutionStatus'
      resolutionStatus: mapBackendStatusToFrontend(item.status),
      // Ensure discrepancyType is a string (in case it's an object)
      discrepancyType: typeof item.discrepancyType === 'string'
        ? item.discrepancyType
        : (item.discrepancyType?.value || item.discrepancyType?.toString() || 'other'),
      // Map nested details fields to flat structure
      description: item.description || item.details?.description,
      financialImpact: item.totalImpact || item.details?.totalImpact,
    }));

    // Also fetch stats
    const statsResponse = await apiClient.get('/v1/discrepancies/stats/summary').catch(() => ({}));
    const rawStats = statsResponse.data || statsResponse || {};

    // Transform stats from API format to frontend format
    // API returns: { bySeverity: { critical: n, ... }, byStatus: { open: n, ... }, totalImpact: n }
    // Frontend expects: { critical: n, high: n, pending: n, totalImpact: n }
    const bySeverity = rawStats.bySeverity || {};
    const byStatus = rawStats.byStatus || {};
    const stats = {
      critical: bySeverity.critical || 0,
      high: bySeverity.high || 0,
      medium: bySeverity.medium || 0,
      low: bySeverity.low || 0,
      // Map backend status counts to frontend status names
      pending: (byStatus.open || 0) + (byStatus.disputed || 0),
      under_review: byStatus.investigating || 0,
      resolved: byStatus.resolved || 0,
      dismissed: byStatus.accepted || 0,
      totalImpact: rawStats.totalImpact || 0,
    };

    setState({
      discrepancies: items,
      pagination: {
        page: pageInfo.page || page,
        totalPages: pageInfo.totalPages || 1,
        totalItems: pageInfo.totalItems || items.length || 0,
      },
      stats: stats,
      filters,
      loading: false,
    });
  } catch (err) {
    console.error('Failed to fetch discrepancies:', err);
    setState({
      discrepancies: [],
      stats: {
        critical: 0,
        high: 0,
        medium: 0,
        low: 0,
        pending: 0,
        under_review: 0,
        resolved: 0,
        totalImpact: 0,
      },
      pagination: {
        page: 1,
        totalPages: 1,
        totalItems: 0,
      },
      loading: false,
      error: err.message || 'Failed to load discrepancies',
    });
  }
}

/**
 * Initialize global handlers
 */
function initHandlers() {
  // View handlers
  window.setDiscrepancyView = (view) => {
    setState({ view });
  };

  // Pagination handlers
  window.goToDiscrepancyPage = (page) => {
    fetchDiscrepancies(page, state.filters);
  };

  // Filter handlers
  window.applyDiscrepancyFilters = () => {
    const severity = document.getElementById('filter-severity')?.value || '';
    const type = document.getElementById('filter-type')?.value || '';
    const status = document.getElementById('filter-status')?.value || '';

    const filters = {};
    if (severity) filters.severity = severity;
    if (type) filters.type = type;
    if (status) filters.status = status;

    fetchDiscrepancies(1, filters);
  };

  window.clearDiscrepancyFilters = () => {
    fetchDiscrepancies(1, {});
  };

  // Action handlers
  window.viewDiscrepancy = (id) => {
    window.location.hash = `discrepancies/${id}`;
  };

  window.resolveDiscrepancy = async (id) => {
    console.log('Resolve discrepancy:', id);
    // Navigate to detail page for resolution workflow
    window.location.hash = `discrepancies/${id}`;
  };

  window.exportDiscrepancies = () => {
    console.log('Export discrepancies');
    // Implement export functionality
  };

  // Initialize component handlers
  initDiscrepancyListHandlers();
}

/**
 * Render the page
 */
function render() {
  const container = document.getElementById('discrepancies-page') || document.getElementById('app-content');
  if (!container) return;

  if (state.loading) {
    container.innerHTML = `
      <div class="page-loading">
        <div class="loading-spinner">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="spinner-icon">
            <path d="M21 12a9 9 0 1 1-6.219-8.56"/>
          </svg>
          <p>${t('common.loading') || 'Loading...'}</p>
        </div>
      </div>
    `;
    return;
  }

  container.innerHTML = `
    <div class="page discrepancies-page">
      ${renderDiscrepancyList({
        discrepancies: state.discrepancies,
        filters: state.filters,
        pagination: state.pagination,
        view: state.view,
        showFilters: true,
        showStats: true,
        stats: state.stats,
      })}
    </div>
  `;
}

/**
 * Page component export
 */
export default {
  /**
   * Initialize the page
   */
  async init() {
    initHandlers();

    // Check for filters from URL query params
    const urlParams = new URLSearchParams(window.location.hash.split('?')[1] || '');
    const initialFilters = {};

    // Support various filter params
    const contractId = urlParams.get('contractId');
    const severity = urlParams.get('severity');
    const type = urlParams.get('type');
    const status = urlParams.get('status');

    if (contractId) initialFilters.contractId = contractId;
    if (severity) initialFilters.severity = severity;
    if (type) initialFilters.type = type;
    if (status) initialFilters.status = status;

    await fetchDiscrepancies(1, initialFilters);
  },

  /**
   * Render the page content
   */
  render() {
    return `
      <div class="page-loading">
        <div class="loading-spinner">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="spinner-icon">
            <path d="M21 12a9 9 0 1 1-6.219-8.56"/>
          </svg>
          <p>${t('common.loading') || 'Loading...'}</p>
        </div>
      </div>
    `;
  },

  /**
   * Called after page is mounted
   */
  async mounted() {
    await this.init();
  },

  /**
   * Cleanup on page leave
   */
  destroy() {
    delete window.setDiscrepancyView;
    delete window.goToDiscrepancyPage;
    delete window.applyDiscrepancyFilters;
    delete window.clearDiscrepancyFilters;
    delete window.viewDiscrepancy;
    delete window.resolveDiscrepancy;
    delete window.exportDiscrepancies;
  },
};
